import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Sparkles } from "lucide-react";

/**
 * Lists check-ins that AI Vision Strict Mode flagged (soft-warn) for the business
 * owner to review. Only renders when at least one flagged item exists.
 */
export default function FlaggedCheckInsCard() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/business/loyalty/flagged-checkins");
      setItems(data.items || []);
    } catch (e) {
      console.warn("flagged check-ins load failed:", e);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!loaded || items.length === 0) return null;

  return (
    <Card className="rounded-md border border-amber-300 bg-amber-50/40 p-6" data-testid="flagged-checkins-card">
      <div className="flex items-center gap-2 flex-wrap">
        <AlertTriangle size={18} className="text-amber-600" />
        <h3 className="font-display text-2xl">Flagged check-in photos</h3>
        <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-amber-400 text-amber-700">
          {items.length}
        </Badge>
      </div>
      <p className="text-sm text-gray-700 mt-1 max-w-2xl">
        AI Vision flagged these check-ins as not matching your photo prompt. They were still recorded (soft-warn mode). Review and dispute any that don&apos;t meet your loyalty terms.
      </p>

      <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="flagged-checkins-list">
        {items.map((c) => (
          <li key={c.id} className="bg-white rounded-md border border-border overflow-hidden shadow-sm" data-testid={`flagged-checkin-${c.id}`}>
            <img src={c.photo_data_url} alt="Flagged check-in" className="w-full aspect-square object-cover" loading="lazy" />
            <div className="p-3 space-y-1.5">
              <p className="text-sm font-medium text-gray-900">{c.customer_name}</p>
              <p className="text-[11px] font-mono text-gray-500">
                {new Date(c.created_at).toLocaleString()} · {c.distance_m}m away
              </p>
              {c.vision_reason && (
                <p className="text-xs text-gray-700 leading-snug">
                  <Sparkles size={10} className="inline -mt-0.5 mr-1 text-amber-600" />
                  {c.vision_reason}
                </p>
              )}
              {c.vision_model && (
                <p className="text-[10px] font-mono text-gray-400">{c.vision_model}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
